import { useState } from "react";
import { ArrowDown, ArrowUp, Check, Eye, EyeOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";

export const tileKey = (t) => t.path || t.to;

export function applyTilePrefs(tiles, prefs) {
  const order = prefs?.order || [];
  const idx = (t) => { const i = order.indexOf(tileKey(t)); return i === -1 ? 1000 + tiles.indexOf(t) : i; };
  return [...tiles].sort((a, b) => idx(a) - idx(b));
}

export function TileEditor({ tiles, prefs, onSaved, onClose }) {
  const [list, setList] = useState(() => applyTilePrefs(tiles, prefs));
  const [hidden, setHidden] = useState(() => new Set(prefs?.hidden || []));
  const [saving, setSaving] = useState(false);
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const n = [...list]; [n[i], n[j]] = [n[j], n[i]]; setList(n); };
  const toggle = (k) => setHidden((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const save = async (reset = false) => {
    setSaving(true);
    try {
      const body = reset ? { order: [], hidden: [] } : { order: list.map(tileKey), hidden: [...hidden] };
      const { data } = await api.put("/auth/tile-prefs", body);
      toast.success(reset ? "Susunan tile dikembalikan ke bawaan" : "Susunan tile disimpan");
      onSaved(data.tile_prefs);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-3 space-y-2" data-testid="tile-editor">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Atur Menu Beranda</div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save(true)} disabled={saving} data-testid="tile-editor-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Bawaan</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onClose} data-testid="tile-editor-cancel">Batal</Button>
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => save(false)} disabled={saving} data-testid="tile-editor-save"><Check className="w-3.5 h-3.5 mr-1" /> Simpan</Button>
        </div>
      </div>
      <ul className="divide-y divide-border">
        {list.map((t, i) => { const k = tileKey(t); const off = hidden.has(k); return (
          <li key={k} className={`flex items-center gap-2 py-1.5 ${off ? "opacity-50" : ""}`} data-testid={`tile-editor-row-${k.replace(/\//g, "-").slice(1)}`}>
            <div className={`w-7 h-7 rounded-lg ${t.bg} text-white flex items-center justify-center shrink-0`}><t.icon className="w-4 h-4" /></div>
            <span className="text-xs flex-1 truncate">{t.label}</span>
            <button type="button" className="p-1 rounded hover:bg-accent disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Naik" data-testid={`tile-up-${k.replace(/\//g, "-").slice(1)}`}><ArrowUp className="w-4 h-4" /></button>
            <button type="button" className="p-1 rounded hover:bg-accent disabled:opacity-30" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Turun" data-testid={`tile-down-${k.replace(/\//g, "-").slice(1)}`}><ArrowDown className="w-4 h-4" /></button>
            <button type="button" className={`p-1 rounded hover:bg-accent ${off ? "text-rose-400" : "text-emerald-400"}`} onClick={() => toggle(k)} aria-label={off ? "Tampilkan" : "Sembunyikan"} data-testid={`tile-toggle-${k.replace(/\//g, "-").slice(1)}`}>{off ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </li>
        ); })}
      </ul>
    </div>
  );
}
